/**
 * One-time migration: normalize `priority` property values.
 *
 * The priority buttons in TaskProperties.tsx wrote 'urgent', while the
 * TaskPriority type — and CanvasEditor, DatabaseWidget, TaskListWidget and
 * iconUtils — all use 'critical'. An `as TaskPriority[]` cast hid the mismatch
 * from the compiler, so tasks prioritized from that panel stored a value
 * nothing else in the app matches on. Filtering by priority silently skipped
 * them, on web and on mobile.
 *
 * The button list is fixed, so no NEW bad values are written. This repairs the
 * rows already stored. Urgency is untouched: 'urgent' is correct there.
 */

import { db } from '../db/database';
import { syncEngine } from '../sync/SyncEngine';
import { getUserId } from '../lib/supabaseRest';

/** priority only — 'urgent' is a legitimate urgency value and must not be touched. */
const LEGACY_PRIORITY = 'urgent';
const CANONICAL_PRIORITY = 'critical';

/** Values arriving from sync can be JSON-stringified ("urgent"). */
function unquote(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    try {
      return String(JSON.parse(trimmed));
    } catch {
      return trimmed.slice(1, -1);
    }
  }
  return trimmed;
}

export async function normalizePriorityValues(): Promise<{
  scanned: number;
  fixed: number;
  errors: string[];
}> {
  console.log('[NormalizePriority] Starting priority value migration...');

  const errors: string[] = [];
  let fixed = 0;

  const allProperties = await db.properties.toArray();
  const priorityProps = allProperties.filter(p => p.name === 'priority');
  console.log(`[NormalizePriority] ${priorityProps.length} priority properties found`);

  const userId = await getUserId();
  if (!userId) {
    return { scanned: priorityProps.length, fixed: 0, errors: ['No authenticated user'] };
  }

  for (const property of priorityProps) {
    if (unquote(property.value) !== LEGACY_PRIORITY) continue;

    try {
      const updatedAt = new Date().toISOString();

      await db.properties.update(property.id, {
        value: CANONICAL_PRIORITY,
        updatedAt,
      });

      // Include every field — a partial payload is what made the earlier
      // null-value repair re-queue broken rows (see fixNullPropertyValues).
      await syncEngine.queueChange('properties', 'update', property.id, {
        object_id: property.objectId,
        name: property.name,
        type: property.type,
        value: CANONICAL_PRIORITY,
        user_id: userId,
        updated_at: updatedAt,
      });

      fixed++;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      errors.push(`${property.id}: ${message}`);
      console.error(`[NormalizePriority] Failed on ${property.id}:`, error);
    }
  }

  console.log(`[NormalizePriority] Done — ${fixed} updated, ${errors.length} errors`);
  return { scanned: priorityProps.length, fixed, errors };
}
